import { dismissUndo, useUndo } from '@/lib/undoStore';
import { RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * "Deleted — Undo" banner. Mounted once in AppShell; driven by the
 * module-scoped undo store so it outlives the component that pushed it.
 * Auto-dismisses at `expiresAt`; clicking Undo runs the entry's `onUndo`
 * and shows its confirmation message briefly.
 */
export function UndoBanner() {
  const entry = useUndo();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ id: number; text: string; error: boolean } | null>(null);

  useEffect(() => {
    if (!entry) return;
    const ms = Math.max(0, entry.expiresAt - Date.now());
    const t = setTimeout(() => dismissUndo(entry.id), ms);
    return () => clearTimeout(t);
  }, [entry]);

  useEffect(() => {
    if (!result) return;
    const t = setTimeout(() => setResult(null), result.error ? 6000 : 3000);
    return () => clearTimeout(t);
  }, [result]);

  if (!entry && !result) return null;

  async function handleUndo() {
    if (!entry || busy) return;
    setBusy(true);
    const id = entry.id;
    try {
      const text = await entry.onUndo();
      setResult({ id, text: text || 'Restored', error: false });
    } catch (e: unknown) {
      setResult({ id, text: (e as Error).message ?? 'Undo failed', error: true });
    } finally {
      setBusy(false);
      dismissUndo(id);
    }
  }

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4"
    >
      {entry ? (
        <div
          role="status"
          className="pointer-events-auto flex items-center gap-3 rounded-xl border border-border bg-surface px-4 py-2.5 text-sm text-foreground ring-1 ring-border elevate"
        >
          <span>{entry.message}</span>
          <button
            type="button"
            onClick={() => void handleUndo()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <RotateCcw className="size-3.5" strokeWidth={1.75} />
            {busy ? 'Restoring…' : entry.undoLabel}
          </button>
          <button
            type="button"
            onClick={() => dismissUndo(entry.id)}
            aria-label="Dismiss"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-3.5" strokeWidth={1.75} />
          </button>
        </div>
      ) : result ? (
        <div
          role="status"
          className={`pointer-events-auto rounded-xl border px-4 py-2.5 text-sm ring-1 ring-border elevate bg-surface ${
            result.error
              ? 'border-destructive/30 text-destructive'
              : 'border-success/30 text-foreground'
          }`}
        >
          {result.text}
        </div>
      ) : null}
    </div>
  );
}
